import { create, destroy } from '@dojo/framework/core/vdom';

import { lccstate, userstate, keymap, childmap } from '@holmescorp/lcc-state';

import { Recommended } from './recommended';

export interface ContentOrder extends Recommended {}
export interface RootContent {
	[key: string]: ContentOrder[];
}

export interface TrackedContent {
	topicset_count: number;
	completed_count: number;
	allcompleted: boolean[];
	content: RootContent;
}

export interface ContentAPI {
	tracked: TrackedContent;
	setup: () => void;
	allcomplete: () => boolean;
	percentComplete: () => number;
	firstIncomplete: (root: string) => string;
	openTopicsets: () => string[];
	recalculate: () => void;
}

const factory = create({ destroy, lccstate, userstate });

export const contentTrack = factory(({ middleware: { destroy, lccstate, userstate } }) => {
	const roots: string[] = lccstate.get(lccstate.path('ditaroots'));
	const handles: any[] = [];
	destroy(() => {
		let handle: any;
		while ((handle = handles.pop())) {
			handle();
		}
	});

	const api: ContentAPI = {
		tracked: {
			topicset_count: 0,
			completed_count: 0,
			allcompleted: [],
			content: {}
		},

		setup(this: ContentAPI): void {
			this.recalculate();
			const store = userstate.getStore();
			const { path } = store;
			const handle = store.onChange(path('status'), () => {
				this.recalculate();
			});
			handles.push(() => handle.remove());
		},

		allcomplete(this: ContentAPI): boolean {
			const { allcompleted } = this.tracked;
			return allcompleted.length > 0 && allcompleted.every((v) => v);
		},

		percentComplete(this: ContentAPI): number {
			const { completed_count, topicset_count } = this.tracked;
			return completed_count / topicset_count;
		},

		recalculate(this: ContentAPI): void {
			const { get, path } = userstate;
			const status = get(path('status'));
			const { tracked } = this;
			tracked.allcompleted = [];
			tracked.topicset_count = 0;
			tracked.completed_count = 0;

			tracked.content = roots.reduce((accum: RootContent, root: string) => {
				const tgfkeys = childmap.get(root)!;
				accum[root] = tgfkeys.map((k): ContentOrder => {
					const [topichead, topicset] = [status[k].parent!, k];
					const basepath = `/explore/${topichead}/${topicset}`;
					const tskey = keymap.get(topicset);
					const title: string = tskey ? tskey.title : '';

					const { data } = status[topicset];
					const completed = data.percent >= 0.8 && data.complete === data.total;
					const isactive = !completed && (data.complete > 0 || data.percent > 0);
					tracked.allcompleted.push(completed);
					tracked.topicset_count += 1;
					tracked.completed_count += completed ? 1 : 0;

					return {
						topichead,
						topicset,
						title,
						basepath,
						percent: data.percent,
						isactive,
						completed
					};
				});
				return accum;
			}, {} as RootContent);
		},

		firstIncomplete(this: ContentAPI, root: string): string {
			const { content } = this.tracked;
			const incomplete = content[root].filter((item) => !item.completed);
			if (incomplete.length > 0) {
				return incomplete[0].topicset;
			}
			return content[root][0].topicset;
		},

		openTopicsets(this: ContentAPI): string[] {
			return roots.map((root) => {
				return this.firstIncomplete(root);
			});
		}
	};

	api.recalculate();

	return api;
});
